import React, { useState, useMemo } from 'react';
import { Plus, LayoutGrid, List, Check, Pencil } from 'lucide-react';
import { Avatar, Badge, Modal, ProductMultiSelect, fmtDate } from '../components/ui.jsx';
import DealModal from '../components/DealModal.jsx';
import { stages, stageColor, money, userById } from '../data.js';

function AddLeadModal({ onClose, onCreate, partners, team, products, accounts }) {
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ account: '', contact: '', value: '', products: products[0] ? [products[0].name] : [], source: 'Internal', owner: team[0]?.id || '', close: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (done) {
    return (
      <Modal title="" onClose={onClose}>
        <div className="modal-success">
          <span className="modal-success-icon"><Check size={24} /></span>
          <h2>Lead created</h2>
          <p>{form.account || 'New lead'} has been added to the pipeline.</p>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </Modal>
    );
  }

  const existingAccount = accounts.find((a) => a.name.toLowerCase() === form.account.trim().toLowerCase());

  return (
    <Modal
      title="Create lead"
      subtitle="Add a new lead or opportunity to the pipeline."
      onClose={onClose}
      actions={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={!existingAccount} onClick={() => { onCreate(form, existingAccount); setDone(true); }}>Create lead</button>
      </>}
    >
      <label>Company name<input list="lead-accounts" placeholder="e.g. Acme Technologies" value={form.account} onChange={set('account')} autoFocus /></label>
      <datalist id="lead-accounts">{accounts.map((a) => <option key={a.id} value={a.name} />)}</datalist>
      {!existingAccount && form.account.trim() && <div className="lh-notice" style={{ marginBottom: 10 }}>Pick an existing account — new companies are created from the Accounts page.</div>}
      <div className="modal-form-row">
        <label>Contact person<input placeholder="Full name" value={form.contact} onChange={set('contact')} /></label>
        <label>Deal value<input placeholder="0" value={form.value} onChange={set('value')} /></label>
      </div>
      <label>Products<ProductMultiSelect value={form.products} options={products.map((p) => p.name)} onChange={(v) => setForm({ ...form, products: v })} /></label>
      <div className="modal-form-row">
        <label>Source<select value={form.source} onChange={set('source')}><option>Internal</option>{partners.map((p) => <option key={p.id}>{p.name}</option>)}</select></label>
        <label>Expected close date<input type="date" value={form.close} onChange={set('close')} /></label>
      </div>
      <label>Owner<select value={form.owner} onChange={set('owner')}>{team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
    </Modal>
  );
}

export default function Leads({ deals = [], onCreateDeal, onUpdateDeal, onOpenDeal, partners = [], team = [], products = [], accounts = [], contacts = [] }) {
  const [view, setView] = useState('table');
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('All');
  const [modal, setModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);

  const filtered = useMemo(() => deals.filter((d) => {
    const matchesSearch = `${d.account} ${d.contact} ${(d.products || []).join(' ')}`.toLowerCase().includes(search.toLowerCase());
    const matchesStage = stageFilter === 'All' || d.stage === stageFilter;
    return matchesSearch && matchesStage;
  }), [deals, search, stageFilter]);

  const createDeal = (form, account) => {
    onCreateDeal({
      accountId: account.id, account: account.name, contact: form.contact || '—', owner: form.owner,
      products: form.products?.length ? form.products : (products[0] ? [products[0].name] : []),
      value: Number(form.value) || 0, stage: 'Proposal', close: form.close || new Date().toISOString().slice(0, 10),
      source: form.source,
    });
  };

  const totalValue = filtered.reduce((s, d) => s + d.value, 0);

  return (
    <>
      <div className="page-head">
        <div className="page-head-left">
          <div className="page-title">Leads & Pipeline</div>
          <div className="page-subtitle">{filtered.length} deals · {money(totalValue)} in view</div>
        </div>
        <div className="page-actions">
          <div className="tabs">
            <span className={`tab-btn ${view === 'table' ? 'active' : ''}`} onClick={() => setView('table')}><List size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Table</span>
            <span className={`tab-btn ${view === 'kanban' ? 'active' : ''}`} onClick={() => setView('kanban')}><LayoutGrid size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Kanban</span>
          </div>
          <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={15} /> Add lead</button>
        </div>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-field"><input placeholder="Search company, contact, product..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
          <select className="filter-select" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
            <option>All</option>
            {stages.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        {view === 'table' ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Company</th><th>Product</th><th>Stage</th><th>Value</th><th>Probability</th><th>Close date</th><th>Source</th><th>Owner</th><th></th></tr></thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.id} className="clickable" onClick={() => onOpenDeal(d)}>
                    <td>
                      <div className="row-entity">
                        <Avatar name={d.account} color={d.tone} size="sm" />
                        <div><div className="row-entity-name">{d.account}</div><div className="row-entity-sub">{d.contact}</div></div>
                      </div>
                    </td>
                    <td>{(d.products || []).join(', ')}</td>
                    <td><Badge tone={stageColor[d.stage]}>{d.stage}</Badge></td>
                    <td className="td-mono">{money(d.value)}</td>
                    <td className="td-muted">{d.probability}%</td>
                    <td className="td-muted">{fmtDate(d.close)}</td>
                    <td className="td-muted">{d.source}</td>
                    <td><Avatar name={userById(team, d.owner)?.name} color={userById(team, d.owner)?.color} size="sm" /></td>
                    <td><button className="btn-icon" title="Edit" onClick={(e) => { e.stopPropagation(); setEditingDeal(d); }}><Pencil size={13} /></button></td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={9} className="table-empty">No deals match your filters.</td></tr>}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card-body">
            <div className="kanban-board">
              {stages.filter((s) => s !== 'Lost').map((stage) => {
                const items = filtered.filter((d) => d.stage === stage);
                return (
                  <div className="kanban-col" key={stage}>
                    <div className="kanban-col-head">
                      <span className="kanban-col-title">{stage.toUpperCase()}</span>
                      <span className="kanban-col-count">{items.length}</span>
                    </div>
                    <div className="kanban-col-body">
                      {items.map((d) => (
                        <div className="kanban-card" key={d.id} onClick={() => onOpenDeal(d)}>
                          <div className="kanban-card-title">{d.account}</div>
                          <div className="row-entity-sub">{(d.products || []).join(', ')} · {d.contact}</div>
                          <div className="kanban-card-meta">
                            <span className="kanban-card-value">{money(d.value)}</span>
                            <Avatar name={userById(team, d.owner)?.name} color={userById(team, d.owner)?.color} size="sm" />
                          </div>
                        </div>
                      ))}
                      {items.length === 0 && <div style={{ fontSize: 11.5, color: 'var(--text-faint)', textAlign: 'center', padding: '16px 0' }}>No deals</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {modal && <AddLeadModal partners={partners} team={team} products={products} accounts={accounts} onClose={() => setModal(false)} onCreate={createDeal} />}
      {editingDeal && (
        <DealModal
          deal={editingDeal}
          accounts={accounts}
          contacts={contacts}
          team={team}
          products={products}
          onClose={() => setEditingDeal(null)}
          onSave={(form) => { onUpdateDeal(editingDeal.id, form); setEditingDeal(null); }}
        />
      )}
    </>
  );
}
